import { ONBOARDING_CALL_URL } from '../services/onboardingCall';
import React from 'react';
import { FOUNDER, hasFounder } from '../constants/founder';
import { Link, useLocation } from 'react-router-dom';
import { Youtube, Instagram, CalendarDays } from 'lucide-react';
import './SiteFooter.css';

const TikTokIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

/**
 * The site's one footer.
 *
 * It lived inline in Homepage.tsx, so every other page ended without links,
 * legal pages, the address or the social accounts — which is part of why the
 * tool pages felt detached from the site.
 *
 * The same trap as the nav applies: the Features and Pricing entries scrolled
 * via document.getElementById, which finds nothing on any page but the
 * homepage. Off the homepage they now navigate to /#features instead of
 * silently doing nothing.
 */
const SiteFooter: React.FC = () => {
  const location = useLocation();
  const onHome = location.pathname === '/';

  const jump = (id: string) => (e: React.MouseEvent) => {
    if (!onHome) return; // let the browser follow /#id
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };
  const sectionHref = (id: string) => (onHome ? `#${id}` : `/#${id}`);

  return (
    <footer className="hp-footer" role="contentinfo">
      <div className="footer-inner">
        <div className="footer-top">
          <div>
            <div className="footer-logo">
              <img src="/shakespeareAI-logo-nobg.png" alt="ShakespeareAI" decoding="async" style={{ width: '24px', height: '24px', verticalAlign: 'middle', marginRight: '8px' }} />
              Shakespeare<em>AI</em>
            </div>
            <p className="footer-desc">The #1 free AI book writer and novel generator. From blank idea to published book &mdash; in one afternoon.</p>
            <div className="footer-badge">Braintastic.ca</div>
            {/* Founder block. The audit scored founder-led trust 1/4: no name,
                no face, no LinkedIn anywhere, against a category leader that
                names its founders. Renders only once constants/founder.ts has
                a real person in it — a stock photo here would be worse than
                the gap. */}
            {hasFounder() && (
              <div className="footer-founder">
                {FOUNDER.photo && (
                  <img src={FOUNDER.photo} alt={FOUNDER.name} width={44} height={44} />
                )}
                <div>
                  <div className="footer-founder-name">{FOUNDER.name}, {FOUNDER.role}</div>
                  <div className="footer-founder-bio">&ldquo;{FOUNDER.bio}&rdquo;</div>
                  {FOUNDER.linkedin && (
                    <a href={FOUNDER.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>
                  )}
                </div>
              </div>
            )}
          </div>

          <nav aria-label="Product links">
            <div className="fc-title">Product</div>
            <div className="fc-links">
              <Link to="/bookwriter">AI Book Generator</Link>
              <Link to="/library">📚 Library</Link>
              <Link to="/my-books">📖 My Books</Link>
              <Link to="/humanize">✨ AI Humanizer</Link>
              <Link to="/translate">🌍 Translator</Link>
              <a href={sectionHref('features')} onClick={jump('features')}>Features</a>
              <a href={sectionHref('pricing')} onClick={jump('pricing')}>Pricing</a>
            </div>
          </nav>

          <nav aria-label="Genre links">
            <div className="fc-title">AI Novel Writer By Genre</div>
            <div className="fc-links">
              {/* Into the writer with the genre already chosen, rather than at a
                  landing page. These used to point at /ai-*, which are static
                  prerendered pages with no React route, so the router catch-all
                  sent every click to the homepage. Values are ChapterBuilder’s
                  own GENRES values; it reads ?genre= on mount. */}
              <Link to="/bookwriter?genre=romance">AI Romance Writer</Link>
              <Link to="/bookwriter?genre=fantasy">AI Fantasy Generator</Link>
              <Link to="/bookwriter?genre=mystery">AI Mystery Writer</Link>
              <Link to="/bookwriter?genre=scifi">AI Sci-Fi Generator</Link>
              <Link to="/bookwriter?genre=thriller">AI Thriller Writer</Link>
            </div>
          </nav>

          <nav aria-label="Audience links">
            <div className="fc-title">For</div>
            <div className="fc-links">
              <a href="/for/authors">Authors</a>
              <a href="/for/content-creators">Content creators</a>
              <a href="/for/coaches">Coaches</a>
              <a href="/for/consultants">Consultants</a>
              <a href="/for/course-creators">Course creators</a>
            </div>
          </nav>

          <nav aria-label="Company links">
            <div className="fc-title">Company</div>
            <div className="fc-links">
              <Link to="/about">About</Link>
              <Link to="/affiliate">Affiliate Program</Link>
              <Link to="/book-signal">📊 Book Signal</Link>
              <Link to="/terms">Terms of Service</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <a href="/contact">Contact Us</a>
            </div>
          </nav>

          <nav aria-label="Resources links">
            <div className="fc-title">Resources</div>
            <div className="fc-links">
              <a href="/blog">Blog</a>
              <a href="/blog/best-ai-writing-tools-authors-2026">Best AI Writing Tools</a>
              <a href="/blog/best-ai-for-writing-novel">Best AI for Writing Novels</a>
              <a href="/blog/how-to-publish-ai-generated-book">How to Publish AI Books</a>
              <a href="/blog/ai-book-writer">AI Book Writer Guide</a>
            </div>
          </nav>

          <div>
            <div className="fc-title">Our Office</div>
            <p className="footer-desc" style={{ marginBottom: 0 }}>8506 Madison Ave<br />Ste A #1019<br />Fair Oaks, CA 95628, United States</p>
            <a className="footer-onboarding-link" href={ONBOARDING_CALL_URL} target="_blank" rel="noopener noreferrer"><CalendarDays size={17} aria-hidden="true" />Book an onboarding call</a>
          </div>
        </div>

        <div className="footer-bottom">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', justifyContent: 'center', marginTop: '0.5rem' }}>
            <a href="https://www.youtube.com/@shakespeare_ai-h2p" target="_blank" rel="noopener noreferrer" aria-label="ShakespeareAI on YouTube"><Youtube size={20} /></a>
            <a href="https://www.instagram.com/shakespeare_ai_agent/" target="_blank" rel="noopener noreferrer" aria-label="ShakespeareAI on Instagram"><Instagram size={20} /></a>
            <a href="https://www.tiktok.com/@shakespeareai/" target="_blank" rel="noopener noreferrer" aria-label="ShakespeareAI on TikTok"><TikTokIcon size={20} /></a>
          </div>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px', marginTop: '0.5rem' }}>
            <span>&copy; 2026 ShakespeareAI &mdash; Free AI Book Writer &amp; Novel Generator · <Link to="/">shakespeareai.net</Link> · <Link to="/affiliate">Affiliate</Link></span>
            <span>Built by <a href="https://braintastic.ca">Braintastic.ca</a></span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
